import { firebase } from "@core/firebase/app";
import { firestoreService } from "@core/services/db/FirestoreService";
import type { ChatMessage, ChatMessageDoc, ChatThread, ChatThreadDoc } from "@core/services/models/Chat";
import { mapMessage, mapThread } from "@core/services/models/Chat";
import { collection, doc, increment, serverTimestamp, setDoc, updateDoc, type DocumentData } from "firebase/firestore";
import type { Unsubscribe } from "firebase/firestore";

const CHATS_COLLECTION = "chats";
const MESSAGES_SUBCOLLECTION = "messages";

class ClientChatService {
  async ensureActiveChatForUser(userId: string): Promise<ChatThread> {
    const existing = await firestoreService.queryCollection(
      CHATS_COLLECTION,
      [
        { field: "status", operator: "==", value: "active" },
        { field: "userId", operator: "==", value: userId }
      ],
      { field: "createdAt", direction: "desc" },
      1
    );

    if (existing.length > 0) {
      const docData = existing[0] as DocumentData & { id: string };
      return mapThread(docData.id, docData as unknown as ChatThreadDoc);
    }

    const now = serverTimestamp();
    const participants: ChatThreadDoc["participants"] = [
      { type: "system", id: "system" },
      { type: "user", id: userId }
    ];

    const chatRef = doc(collection(firebase.firestore, CHATS_COLLECTION));
    const chatDoc: ChatThreadDoc = {
      status: "active",
      userId,
      anonSessionId: null,
      linkedSessionIds: [],
      participants,
      createdAt: now,
      updatedAt: now,
      lastMessageAt: null,
      lastMessage: null,
      unreadCounts: {
        user: 0,
        admin: 0
      },
      metadata: {
        source: "client-app"
      },
      redirectToChatId: null
    };

    await setDoc(chatRef, chatDoc as unknown as DocumentData);

    const created = await firestoreService.getDocument(CHATS_COLLECTION, chatRef.id);
    if (!created) {
      throw new Error("Failed to create chat thread");
    }
    return mapThread(created.id, created as unknown as ChatThreadDoc);
  }

  subscribeToChat(chatId: string, callback: (thread: ChatThread | null) => void): Unsubscribe {
    return firestoreService.subscribeToDocument(CHATS_COLLECTION, chatId, (docSnapshot) => {
      if (!docSnapshot) {
        callback(null);
        return;
      }
      callback(mapThread(docSnapshot.id, docSnapshot as unknown as ChatThreadDoc));
    });
  }

  subscribeToMessages(chatId: string, callback: (messages: ChatMessage[]) => void, limitCount = 200): Unsubscribe {
    return firestoreService.subscribeToCollection(
      `${CHATS_COLLECTION}/${chatId}/${MESSAGES_SUBCOLLECTION}`,
      [],
      { field: "createdAt", direction: "asc" },
      limitCount,
      (docs) => {
        const mapped = docs.map((docItem) => mapMessage(docItem.id, docItem as unknown as ChatMessageDoc));
        callback(mapped);
      }
    );
  }

  async sendMessage(chatId: string, text: string, senderId: string): Promise<void> {
    const trimmed = text.trim();
    if (!trimmed) {
      return;
    }

    const now = serverTimestamp();
    const message: ChatMessageDoc = {
      chatId,
      senderId,
      senderType: "user",
      text: trimmed,
      createdAt: now,
      deliveryStatus: "sent",
      isReadByUser: true,
      isReadByAdmin: false
    };

    const messagesCollection = collection(firebase.firestore, CHATS_COLLECTION, chatId, MESSAGES_SUBCOLLECTION);
    await setDoc(doc(messagesCollection), message as unknown as DocumentData);

    const chatRef = doc(firebase.firestore, CHATS_COLLECTION, chatId);
    await updateDoc(chatRef, {
      lastMessage: {
        preview: trimmed,
        senderType: "user",
        createdAt: now
      },
      lastMessageAt: now,
      updatedAt: now,
      "unreadCounts.admin": increment(1)
    });
  }
}

export const chatService = new ClientChatService();

